import { DEFAULT_CANVAS_BACKGROUND, DEFAULT_CARD_DURATION_MS, type EtsyDataSource, type CarouselVariant, type ExperienceVariant, type ProductCardEdit, type ProductSelection, type SelectedProduct } from "./contracts.js";
import { importEtsyShop } from "./import-shop.js";
import { ProductSelectionError, selectProductsForVideo } from "./select-products.js";
import { createShopVideoProject, type VideoHeadlineOptions } from "./shop-video-project.js";
import { DEFAULT_EXPORT_FORMAT, type ExportFormat } from "./export-formats.js";

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

const MINIMUM_CARD_COUNT = 3;
const MAXIMUM_CARD_COUNT = 8;

function applyEdit(selected: SelectedProduct, edit: ProductCardEdit): SelectedProduct {
  return {
    ...selected,
    product: {
      ...selected.product,
      primaryImage: edit.image.trim() || selected.product.primaryImage,
      title: edit.title.trim(),
      price: edit.price.trim(),
    },
    cardLayout: edit.layout === "catalogue" ? "catalogue" : "overlay",
    imageTransform: {
      zoom: clamp(edit.zoom, 1, 3),
      offsetX: clamp(edit.offsetX, -100, 100),
      offsetY: clamp(edit.offsetY, -100, 100),
    },
  };
}

function createCustomProduct(edit: ProductCardEdit): SelectedProduct {
  return applyEdit({
    product: {
      id: edit.productId,
      title: edit.title.trim(),
      price: edit.price.trim(),
      listingUrl: "",
      primaryImage: edit.image.trim(),
      alternateImages: [],
      imageWidth: 1200,
      imageHeight: 1600,
      favorites: 0,
      featured: false,
    },
    score: 0,
    reasons: [],
  }, edit);
}

function applyProductEdits(selection: ProductSelection, edits: ProductCardEdit[], deletedProductIds: string[]): ProductSelection {
  const byId = new Map(edits.map((edit) => [edit.productId, edit]));
  const deleted = new Set(deletedProductIds);
  const selected = selection.selected
    .filter(({ product }) => !deleted.has(product.id))
    .map((item) => byId.has(item.product.id) ? applyEdit(item, byId.get(item.product.id)!) : item);
  const included = new Set(selected.map(({ product }) => product.id));
  for (const edit of edits) {
    if (!edit.isNew || deleted.has(edit.productId) || included.has(edit.productId) || selected.length >= MAXIMUM_CARD_COUNT) continue;
    selected.push(createCustomProduct(edit));
    included.add(edit.productId);
  }
  if (selected.length < MINIMUM_CARD_COUNT) throw new ProductSelectionError(`Keep at least ${MINIMUM_CARD_COUNT} cards in the carousel.`);
  return {
    ...selection,
    requested: selected.length,
    selected: selected.slice(0, MAXIMUM_CARD_COUNT),
  };
}

export async function createShopVideoDraft(input: string, dataSource: EtsyDataSource, carouselVariant: CarouselVariant = "curved-focus-carousel", exportFormat: ExportFormat = DEFAULT_EXPORT_FORMAT, experience: ExperienceVariant = "etsy", productEdits: ProductCardEdit[] = [], deletedProductIds: string[] = [], cardDurationMs = DEFAULT_CARD_DURATION_MS, canvasBackground = DEFAULT_CANVAS_BACKGROUND, videoHeadline?: VideoHeadlineOptions) {
  const shop = await importEtsyShop(input, dataSource);
  const selection = applyProductEdits(selectProductsForVideo(shop.products, 5), productEdits, deletedProductIds);
  const { project, duration } = createShopVideoProject(shop, selection, carouselVariant, exportFormat, experience, cardDurationMs, canvasBackground, videoHeadline);
  return { shop, selection, project, duration };
}
